import React, { useState } from "react";
import * as Yup from "yup";
import { Formik, Form } from "formik";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import CircularProgress from "@mui/material/CircularProgress";

import useMutation from "../../hooks/useMutation";
import useNotification from "../../hooks/notification";
import ConfirmDeleteModal from "../../components/images/ConfirmDeleteModal";
import {
  CUSTOM_FIELD_TEMPLATE_UPDATE,
  CUSTOM_FIELD_TEMPLATE_DESTROY,
} from "../../constants/graphql/mutations";
import { CUSTOM_FIELD_TEMPLATES_QUERY } from "../../constants/graphql/queries";
import ConfigurableFieldFormStep from "./ConfigurableFieldFormStep";

const styles = {
  dialogTitle: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    position: "relative",
    padding: 2,
  },
  closeButton: {
    position: "absolute",
    right: 8,
    top: 8,
  },
  dialogContent: {
    paddingTop: 3,
  },
  formField: {
    marginBottom: 2,
  },
  actions: {
    display: "flex",
    justifyContent: "space-between",
    px: 3,
    py: 2,
  },
};

const validationSchema = Yup.object().shape({
  fieldType: Yup.string().required("Field Type is required"),
  defaultLabel: Yup.string().required("Default Label is required"),
});

const refetchQueries = [
  {
    query: CUSTOM_FIELD_TEMPLATES_QUERY,
  },
  {
    query: CUSTOM_FIELD_TEMPLATES_QUERY,
    variables: { sortBy: "default_label", sortDirection: "asc" },
  },
];

export default function EditConfigurableFieldDialog({ customFieldTemplate, onClose }) {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [updateCustomFieldTemplate, { loading }] = useMutation(CUSTOM_FIELD_TEMPLATE_UPDATE, {
    refetchQueries,
    awaitRefetchQueries: true,
  });
  const [destroyCustomFieldTemplate] = useMutation(CUSTOM_FIELD_TEMPLATE_DESTROY, {
    refetchQueries,
    awaitRefetchQueries: true,
  });
  const { notifySuccess, notifyError } = useNotification();

  const handleSubmit = async values => {
    try {
      const variables = {
        id: customFieldTemplate.id,
        defaultLabel: values.defaultLabel,
        defaultValue: values.defaultValue,
        fieldType: values.fieldType,
        availableOptions: values.availableOptions,
        restrictToAvailableOptions: values.restrictToAvailableOptions,
        autoAddCustomValues: values.autoAddCustomValues,
        enableBarcodeScanner: values.enableBarcodeScanner,
        multipleValues: values.multipleValues,
      };

      await updateCustomFieldTemplate({
        variables,
        onSuccess: () => {
          notifySuccess("Configurable field updated successfully");
          handleClose();
        },
        onFailure: errors => {
          notifyError(errors);
        },
      });
    } catch (error) {
      console.error("Error updating custom field:", error);
      notifyError("Failed to update configurable field");
    }
  };

  const handleClose = () => {
    onClose();
  };

  const handleOpenDeleteModal = () => setShowDeleteModal(true);
  const handleCloseDeleteModal = () => setShowDeleteModal(false);

  const handleDelete = async () => {
    if (!customFieldTemplate) return;
    await destroyCustomFieldTemplate({
      variables: { id: customFieldTemplate.id },
      onSuccess: () => {
        notifySuccess("Configurable field deleted");
        handleClose();
      },
      onFailure: errors => {
        notifyError(errors);
      },
    });
  };

  if (!customFieldTemplate) return null;

  return (
    <Formik
      initialValues={{
        fieldType: customFieldTemplate.fieldType || "",
        defaultLabel: customFieldTemplate.defaultLabel || "",
        defaultValue: customFieldTemplate.defaultValue || "",
        availableOptions: customFieldTemplate.availableOptions || [],
        restrictToAvailableOptions: customFieldTemplate.restrictToAvailableOptions || false,
        autoAddCustomValues: customFieldTemplate.autoAddCustomValues || false,
        enableBarcodeScanner: customFieldTemplate.enableBarcodeScanner || false,
        multipleValues: customFieldTemplate.multipleValues || false,
      }}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ isValid, handleSubmit }) => (
        <Form>
          <Dialog open onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={styles.dialogTitle}>
              <Typography fontSize={24} fontWeight={500} color="textPrimary">
                Edit Configurable Field
              </Typography>
              <IconButton onClick={handleClose} sx={styles.closeButton}>
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent sx={styles.dialogContent}>
              <ConfigurableFieldFormStep disableFieldType />
            </DialogContent>
            <DialogActions sx={styles.actions}>
              <Button color="error" variant="outlined" onClick={handleOpenDeleteModal}>
                Delete
              </Button>
              <Box display="flex" gap={1}>
                <Button variant="outlined" onClick={handleClose}>
                  Cancel
                </Button>
                <Button
                  onClick={handleSubmit}
                  variant="contained"
                  color="primary"
                  disabled={loading || !isValid}
                  startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
                >
                  Save
                </Button>
              </Box>
            </DialogActions>
          </Dialog>
          {showDeleteModal && (
            <ConfirmDeleteModal
              title="Delete Configurable Field"
              descr={`Are you sure you want to delete the configurable field "${customFieldTemplate.defaultLabel}"?`}
              onClose={handleCloseDeleteModal}
              onDelete={handleDelete}
              applyLabel="Delete"
            />
          )}
        </Form>
      )}
    </Formik>
  );
}
